import React from "react";
import { useAppTheme } from "../../theme/AppThemeContext.jsx";
import {
  Box,
  Popover,
  Paper,
  Typography,
  IconButton,
  ButtonBase,
  TextField,
  Divider,
  ClickAwayListener,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";

/**
 * ReusableDatePicker
 * ------------------
 * Flow exactly as you described (NOT "two modes"):
 * 1) Clicking the date field opens the popover with:
 *    - Title "Date"
 *    - Manual input (defaults to TODAY if value is null/undefined)
 *    - Calendar grid for the currently selected month
 *    - Header "February 2026" with a chevron NEXT TO THE YEAR
 * 2) Clicking that chevron opens the Month/Year wheel selector.
 *    - Selecting month/year returns you to the calendar grid, now showing that month/year.
 *    - Then you pick the day in the grid.
 *
 * Reusable: you pass value + onChange, anchorEl/open/onClose.
 */

export function ReusableDatePicker({
  value,
  onChange,
  open,
  anchorEl,
  onClose,
  title = "Date",
}) {
  const { tokens } = useAppTheme();
  const primaryRgb = tokens.primary.slice(4, -1);
  const muted = "rgb(133, 133, 133)";
  const textDark = "rgb(20, 20, 20)";
  const border = "1px solid rgba(0,0,0,0.06)";

  const initial = React.useMemo(() => clampToMidnight(value ?? new Date()), [value]);

  const [wheelOpen, setWheelOpen] = React.useState(false);
  const [isMonthYearView, setIsMonthYearView] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState(initial);
  const [viewMonth, setViewMonth] = React.useState(() => startOfMonth(initial));
  const [inputValue, setInputValue] = React.useState(() => formatDDMMYYYY(initial));

  React.useEffect(() => {
    if (!open) return;

    const base = clampToMidnight(value ?? new Date());
    setWheelOpen(false);
    setIsMonthYearView(false);
    setSelectedDate(base);
    setViewMonth(startOfMonth(base));
    setInputValue(formatDDMMYYYY(base));
  }, [open, value]);

  function commit(d) {
    const next = clampToMidnight(d);
    setSelectedDate(next);
    setViewMonth(startOfMonth(next));
    setInputValue(formatDDMMYYYY(next));
    onChange(next);
  }

  function handleInputCommit() {
    const parsed = parseDDMMYYYY(inputValue);
    if (!parsed) return; // keep as-is; you can show a validation error if you want
    commit(parsed);
  }

  // Toggle between calendar grid and month/year view
  const handleViewToggle = () => {
    setIsMonthYearView(!isMonthYearView);
  };

  // Handle month selection in month/year view
  const handleMonthSelect = (monthIndex) => {
    const newMonth = new Date(viewMonth);
    newMonth.setMonth(monthIndex);
    setViewMonth(startOfMonth(newMonth));
  };

  // Handle year selection in month/year view
  const handleYearSelect = (year) => {
    const newYear = new Date(viewMonth);
    newYear.setFullYear(year);
    setViewMonth(startOfMonth(newYear));
  };

  const monthLabel = monthName(viewMonth.getMonth());
  const yearLabel = String(viewMonth.getFullYear());

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={() => {
        setWheelOpen(false);
        setIsMonthYearView(false);
        onClose();
      }}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{
        elevation: 0,
        sx: {
          mt: 1,
          borderRadius: 4,
          border,
          boxShadow: "0px 10px 28px rgba(0,0,0,0.14)",
          overflow: "hidden",
        },
      }}
    >
      <Paper sx={{ width: 320, background: "white" }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900, color: textDark }}>
            {title}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => {
                setWheelOpen(false);
                setIsMonthYearView(false);
                onClose();
              }}
              aria-label="Close date picker"
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                border,
                background: "white",
                "&:hover": { background: "white" },
              }}
            >
              <CloseIcon fontSize="small" sx={{ color: muted }} />
            </IconButton>
          </Box>
        </Box>

        {/* Manual input (defaults to today on open) */}
        <Box sx={{ px: 2, pb: 1.5 }}>
          <TextField
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleInputCommit}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleInputCommit();
            }}
            placeholder="DD/MM/YYYY"
            fullWidth
            inputProps={{ inputMode: "numeric" }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                background: "white",
                "& fieldset": { borderColor: `rgba(${primaryRgb}, 0.65)` },
                "&:hover fieldset": { borderColor: `rgba(${primaryRgb}, 0.85)` },
                "&.Mui-focused fieldset": { borderColor: tokens.primary, borderWidth: 2 },
              },
              "& input": { fontWeight: 400, letterSpacing: 0.3 },
            }}
          />
        </Box>

        {/* Month/Year header + chevron beside YEAR */}
        <Box sx={{ px: 2, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 900, color: textDark }}>
              {monthLabel}
            </Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 900, color: tokens.primary }}>
              {yearLabel}
            </Typography>

            {/* This is the specific chevron you described: clicking toggles view */}
            <IconButton
              size="small"
              onClick={handleViewToggle}
              aria-label="Toggle month year view"
              sx={{
                ml: 0.25,
                width: 28,
                height: 28,
                borderRadius: "50%",
                color: tokens.primary,
              }}
            >
              {isMonthYearView ? (
                <ExpandMoreOutlinedIcon />
              ) : (
                <ChevronRightRoundedIcon />
              )}
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(0,0,0,0.05)" }} />

        {/* Body: either Calendar Grid or Month/Year columns */}
        <Box sx={{ px: 2, py: 2 }}>
          {!isMonthYearView ? (
            <CalendarGrid
              selectedDate={selectedDate}
              viewMonth={viewMonth}
              onPickDay={(d) => commit(d)}
              onChangeViewMonth={(m) => setViewMonth(m)}
            />
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, alignItems: "center", py: 1 }}>
              <ScrollingMonthColumn
                selectedMonth={viewMonth.getMonth()}
                onMonthSelect={handleMonthSelect}
              />
              <ScrollingYearColumn
                selectedYear={viewMonth.getFullYear()}
                onYearSelect={handleYearSelect}
              />
            </Box>
          )}
        </Box>
      </Paper>
    </Popover>
  );
}

/* -------------------------------- Calendar Grid -------------------------------- */

function CalendarGrid({
  selectedDate,
  viewMonth,
  onPickDay,
  onChangeViewMonth,
}) {
  const { tokens } = useAppTheme();
  const muted = "rgb(133, 133, 133)";
  const textDark = "rgb(20, 20, 20)";

  const first = startOfMonth(viewMonth);
  const last = endOfMonth(viewMonth);

  const leading = first.getDay(); // 0..6 (Sun..Sat)
  const totalDays = last.getDate();

  const cells = [];

  // leading filler
  for (let i = 0; i < leading; i++) {
    const d = new Date(first);
    d.setDate(1 - (leading - i));
    cells.push({ date: d, inMonth: false });
  }

  // in-month
  for (let day = 1; day <= totalDays; day++) {
    cells.push({ date: new Date(first.getFullYear(), first.getMonth(), day), inMonth: true });
  }

  // trailing filler to make 6 rows (42 cells)
  while (cells.length < 42) {
    const d = new Date(last);
    d.setDate(last.getDate() + (cells.length - (leading + totalDays) + 1));
    cells.push({ date: d, inMonth: false });
  }

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const sel = clampToMidnight(selectedDate);

  return (
    <Box>
      {/* DOW */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
        {daysOfWeek.map((d) => (
          <Typography key={d} sx={{ textAlign: "center", fontSize: 12, fontWeight: 800, color: muted, py: 0.5 }}>
            {d}
          </Typography>
        ))}
      </Box>

      {/* Grid */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
        {cells.map(({ date, inMonth }, idx) => {
          const isSel =
            inMonth &&
            date.getFullYear() === sel.getFullYear() &&
            date.getMonth() === sel.getMonth() &&
            date.getDate() === sel.getDate();

          return (
            <ButtonBase
              key={`${date.toISOString()}-${idx}`}
              onClick={() => onPickDay(date)}
              sx={{
                height: 34,
                borderRadius: 999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: inMonth ? 1 : 0.22,
                backgroundColor: isSel ? "rgba(244,159,153,0.35)" : "transparent",
                "&:hover": {
                  backgroundColor: isSel ? "rgba(244,159,153,0.42)" : "rgba(0,0,0,0.04)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: isSel ? 900 : 800,
                  color: isSel ? tokens.primary : textDark,
                }}
              >
                {date.getDate()}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>

      {/* month nav (subtle, optional; you can remove if you want it stricter) */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <ButtonBase onClick={() => onChangeViewMonth(addMonths(viewMonth, -1))} sx={{ borderRadius: 2, px: 1, py: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: muted }}>Prev</Typography>
        </ButtonBase>
        <ButtonBase onClick={() => onChangeViewMonth(addMonths(viewMonth, 1))} sx={{ borderRadius: 2, px: 1, py: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: muted }}>Next</Typography>
        </ButtonBase>
      </Box>
    </Box>
  );
}

/* ------------------------------ Month/Year Wheel ------------------------------ */

function MonthYearWheel({
  baseDate,
  viewMonth,
  onPick,
  onCloseWheel,
}) {
  const textDark = "rgb(20, 20, 20)";
  const faded = "rgba(0,0,0,0.25)";
  
  // Manage independent state for month and year selection
  const [selectedMonthIdx, setSelectedMonthIdx] = React.useState(viewMonth.getMonth());
  const [selectedYear, setSelectedYear] = React.useState(viewMonth.getFullYear());
  
  const months = React.useMemo(
    () => Array.from({ length: 12 }, (_, i) => monthName(i)),
    []
  );

  // Keep a tight year range like your screenshot, but feel free to widen
  const years = React.useMemo(() => {
    const y = baseDate.getFullYear();
    const start = Math.max(2000, y - 10); // Expand range for better scrolling
    const end = Math.min(2100, y + 10);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [baseDate]);

  const selectedYearIndex = Math.max(0, years.indexOf(selectedYear));

  // Update state when viewMonth changes (when wheel opens)
  React.useEffect(() => {
    setSelectedMonthIdx(viewMonth.getMonth());
    setSelectedYear(viewMonth.getFullYear());
  }, [viewMonth]);

  // Handle scroll events for month and year columns
  const handleMonthScroll = (event) => {
    const scrollElement = event.target;
    const itemHeight = 30; // Approximate height of each item
    const scrollTop = scrollElement.scrollTop;
    const newIdx = Math.round(scrollTop / itemHeight);
    const clampedIdx = Math.max(0, Math.min(months.length - 1, newIdx));
    setSelectedMonthIdx(clampedIdx);
  };

  const handleYearScroll = (event) => {
    const scrollElement = event.target;
    const itemHeight = 30; // Approximate height of each item
    const scrollTop = scrollElement.scrollTop;
    const newIdx = Math.round(scrollTop / itemHeight);
    const clampedIdx = Math.max(0, Math.min(years.length - 1, newIdx));
    const newYear = years[clampedIdx];
    setSelectedYear(newYear);
  };

  // Apply selection when clicking outside
  const handleApplySelection = () => {
    onPick(selectedYear, selectedMonthIdx);
  };

  // Handle click outside to apply selection and close wheel
  const handleClickAway = () => {
    handleApplySelection();
    onCloseWheel();
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ minHeight: 190 }}>
        {/* No explicit "mode switch" UI; it appears ONLY because chevron was clicked */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, alignItems: "center" }}>
          <ScrollingWheelColumn
            items={months}
            selectedIndex={selectedMonthIdx}
            onScroll={handleMonthScroll}
            render={(item, isSel, opacity) => (
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: isSel ? 900 : 700,
                  color: isSel ? textDark : faded,
                  opacity,
                  lineHeight: "30px",
                  textAlign: "center",
                  py: 0.5,
                }}
              >
                {item}
              </Typography>
            )}
          />

          <ScrollingWheelColumn
            items={years.map(String)}
            selectedIndex={selectedYearIndex}
            onScroll={handleYearScroll}
            render={(item, isSel, opacity) => (
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: isSel ? 900 : 700,
                  color: isSel ? textDark : faded,
                  opacity,
                  lineHeight: "30px",
                  textAlign: "center",
                  py: 0.5,
                }}
              >
                {item}
              </Typography>
            )}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <ButtonBase onClick={onCloseWheel} sx={{ borderRadius: 2, px: 1, py: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "rgba(0,0,0,0.45)" }}>
              Back to calendar
            </Typography>
          </ButtonBase>
        </Box>
      </Box>
    </ClickAwayListener>
  );
}

function ScrollingWheelColumn({
  items,
  selectedIndex,
  onScroll,
  render,
}) {
  const { tokens } = useAppTheme();
  const primaryRgb = tokens.primary.slice(4, -1);
  const scrollRef = React.useRef(null);
  const itemHeight = 30; // Height of each item in pixels
  const visibleItems = 7; // Number of items visible at once
  const containerHeight = itemHeight * visibleItems;

  // Scroll to selected item when component mounts or selection changes
  React.useEffect(() => {
    if (scrollRef.current) {
      const scrollTop = selectedIndex * itemHeight;
      scrollRef.current.scrollTop = scrollTop;
    }
  }, [selectedIndex, itemHeight]);

  return (
    <Box sx={{ position: 'relative', height: containerHeight }}>
      <Box
        ref={scrollRef}
        onScroll={onScroll}
        sx={{
          height: containerHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin', // For Firefox
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        {/* Add padding at top and bottom for better scrolling experience */}
        <Box sx={{ height: itemHeight * 3 }} />
        
        {items.map((item, idx) => {
          const dist = Math.abs(idx - selectedIndex);
          const opacity = dist === 0 ? 1 : dist === 1 ? 0.7 : dist === 2 ? 0.4 : dist === 3 ? 0.2 : 0.1;
          const isSel = idx === selectedIndex;

          return (
            <Box
              key={`${item}-${idx}`}
              sx={{
                height: itemHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: tokens.primarySubtle,
                  transform: 'scale(1.02)',
                },
              }}
              onClick={() => {
                // Allow clicking to select as well
                if (scrollRef.current) {
                  scrollRef.current.scrollTop = idx * itemHeight;
                }
              }}
            >
              {render(item, isSel, opacity)}
            </Box>
          );
        })}
        
        <Box sx={{ height: itemHeight * 3 }} />
      </Box>
      
      {/* Center indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: itemHeight,
          transform: 'translateY(-50%)',
          borderTop: `1px solid rgba(${primaryRgb}, 0.3)`,
          borderBottom: `1px solid rgba(${primaryRgb}, 0.3)`,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

/* --------------------------------- Helpers --------------------------------- */

function clampToMidnight(d) {
  if (!d) return new Date();
  
  // Convert to Date object if it's not already
  const date = d instanceof Date ? d : new Date(d);
  
  // Check if it's a valid date
  if (isNaN(date.getTime())) return new Date();
  
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}
function startOfMonth(d) {
  if (!d) return new Date();
  
  // Convert to Date object if it's not already
  const date = d instanceof Date ? d : new Date(d);
  
  // Check if it's a valid date
  if (isNaN(date.getTime())) return new Date();
  
  return new Date(date.getFullYear(), date.getMonth(), 1);
}
function endOfMonth(d) {
  if (!d) return new Date();
  
  // Convert to Date object if it's not already
  const date = d instanceof Date ? d : new Date(d);
  
  // Check if it's a valid date
  if (isNaN(date.getTime())) return new Date();
  
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}
function addMonths(d, n) {
  if (!d) return new Date();
  
  // Convert to Date object if it's not already
  const date = d instanceof Date ? d : new Date(d);
  
  // Check if it's a valid date
  if (isNaN(date.getTime())) return new Date();
  
  return new Date(date.getFullYear(), date.getMonth() + n, 1);
}
function daysInMonth(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}
function setMonthYearKeepDay(d, year, monthIndex0) {
  const day = Math.min(d.getDate(), daysInMonth(year, monthIndex0));
  return new Date(year, monthIndex0, day);
}
function monthName(monthIndex0) {
  return new Date(2020, monthIndex0, 1).toLocaleString(undefined, { month: "long" });
}
function pad2(n) {
  return String(n).padStart(2, "0");
}
function formatDDMMYYYY(d) {
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}
function parseDDMMYYYY(s) {
  const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const dd = Number(m[1]);
  const mm = Number(m[2]);
  const yy = Number(m[3]);
  if (mm < 1 || mm > 12) return null;
  const dim = daysInMonth(yy, mm - 1);
  if (dd < 1 || dd > dim) return null;
  return new Date(yy, mm - 1, dd);
}

// ------------------------------
// Scrolling Month Column
// ------------------------------
function ScrollingMonthColumn({ selectedMonth, onMonthSelect }) {
  const { tokens } = useAppTheme();
  const primaryRgb = tokens.primary.slice(4, -1);
  const scrollRef = React.useRef(null);
  const itemHeight = 30;
  const visibleItems = 7;
  const containerHeight = itemHeight * visibleItems;

  const months = React.useMemo(
    () => Array.from({ length: 12 }, (_, i) =>
      new Date(2000, i, 1).toLocaleString(undefined, { month: "long" })
    ),
    []
  );

  // Scroll to selected month when component mounts or selection changes
  React.useEffect(() => {
    if (scrollRef.current) {
      const scrollTop = selectedMonth * itemHeight;
      scrollRef.current.scrollTop = scrollTop;
    }
  }, [selectedMonth, itemHeight]);

  const handleScroll = (event) => {
    const scrollElement = event.target;
    const scrollTop = scrollElement.scrollTop;
    const newMonthIndex = Math.round(scrollTop / itemHeight);
    const clampedIndex = Math.max(0, Math.min(11, newMonthIndex));
    onMonthSelect(clampedIndex);
  };

  return (
    <Box sx={{ position: 'relative', height: containerHeight }}>
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          height: containerHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Box sx={{ height: itemHeight * 3 }} />
        
        {months.map((month, idx) => {
          const isSel = idx === selectedMonth;
          const opacity = isSel ? 1 : 0.6;

          return (
            <Box
              key={month}
              sx={{
                height: itemHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: tokens.primarySubtle,
                  transform: 'scale(1.02)',
                },
              }}
              onClick={() => {
                if (scrollRef.current) {
                  scrollRef.current.scrollTop = idx * itemHeight;
                }
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: isSel ? 900 : 700,
                  color: isSel ? tokens.primary : 'rgb(20, 20, 20)',
                  opacity,
                  lineHeight: "30px",
                  textAlign: "center",
                  py: 0.5,
                }}
              >
                {month}
              </Typography>
            </Box>
          );
        })}

        <Box sx={{ height: itemHeight * 3 }} />
      </Box>

      {/* Center indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: itemHeight,
          transform: 'translateY(-50%)',
          borderTop: `1px solid rgba(${primaryRgb}, 0.3)`,
          borderBottom: `1px solid rgba(${primaryRgb}, 0.3)`,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

// ------------------------------
// Scrolling Year Column
// ------------------------------
function ScrollingYearColumn({ selectedYear, onYearSelect }) {
  const { tokens } = useAppTheme();
  const primaryRgb = tokens.primary.slice(4, -1);
  const scrollRef = React.useRef(null);
  const itemHeight = 30;
  const visibleItems = 7;
  const containerHeight = itemHeight * visibleItems;

  const currentYear = new Date().getFullYear();
  const years = React.useMemo(() => {
    const start = currentYear; // Current year
    const end = currentYear + 20; // 20 years ahead
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, []);

  const selectedYearIndex = Math.max(0, years.indexOf(selectedYear));

  // Scroll to selected year when component mounts or selection changes
  React.useEffect(() => {
    if (scrollRef.current) {
      const scrollTop = selectedYearIndex * itemHeight;
      scrollRef.current.scrollTop = scrollTop;
    }
  }, [selectedYearIndex, itemHeight]);

  const handleScroll = (event) => {
    const scrollElement = event.target;
    const scrollTop = scrollElement.scrollTop;
    const newYearIndex = Math.round(scrollTop / itemHeight);
    const clampedIndex = Math.max(0, Math.min(years.length - 1, newYearIndex));
    onYearSelect(years[clampedIndex]);
  };

  return (
    <Box sx={{ position: 'relative', height: containerHeight }}>
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          height: containerHeight,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '3px',
            '&:hover': {
              background: 'rgba(0, 0, 0, 0.5)',
            },
          },
        }}
      >
        <Box sx={{ height: itemHeight * 3 }} />
        
        {years.map((year) => {
          const isSel = year === selectedYear;
          const opacity = isSel ? 1 : 0.6;

          return (
            <Box
              key={year}
              sx={{
                height: itemHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: tokens.primarySubtle,
                  transform: 'scale(1.02)',
                },
              }}
              onClick={() => {
                const yearIndex = years.indexOf(year);
                if (scrollRef.current && yearIndex >= 0) {
                  scrollRef.current.scrollTop = yearIndex * itemHeight;
                }
              }}
            >
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: isSel ? 900 : 700,
                  color: isSel ? tokens.primary : 'rgb(20, 20, 20)',
                  opacity,
                  lineHeight: "30px",
                  textAlign: "center",
                  py: 0.5,
                }}
              >
                {year}
              </Typography>
            </Box>
          );
        })}

        <Box sx={{ height: itemHeight * 3 }} />
      </Box>

      {/* Center indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          right: 0,
          height: itemHeight,
          transform: 'translateY(-50%)',
          borderTop: `1px solid rgba(${primaryRgb}, 0.3)`,
          borderBottom: `1px solid rgba(${primaryRgb}, 0.3)`,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}
